import pager from './pager.js';
import {DEFAULT_DATASET} from './datasets.js';
import {
  getFeatureReference,
  getLink,
  parseReference,
  referenceToUrl,
} from './util.js';
import {graneroInstance, processAPIInstance} from './index.js';

/**
 * Provides methods for working with Granero features.
 * @module api/features
 */

/**
 *
 * @param {String} collectionId
 * @param {Object} options
 * @param {number} options.maxItems Limit the result set to this size (by default it equals limit, set to Infinity to load all).
 * @param {function(Array,Boolean,Function)} options.each A function that is called once for
 *     each page of data. The each callback takes 3 arguments. The first is a response object.
 *     The second is a boolean representing whether there are more results, the third is a function that gets
 *     the next page of results. If `each` returns false, it prevents automatic paging.
 *     If the `each` callback is absent, all data will be concatenated and provided when the promise resolves.
 * @returns {Promise<Array>} A promise that resolves when all data is finished
 *     loading or is rejected with any error.  If an `each` callback is not
 *     provided, the promise will resolve with all data concatenated.
 */
export const getFeatures = (collectionId, options) => {
  const {dataset = DEFAULT_DATASET, params, ...otherOptions} = options || {};

  const config = {
    url: `/ogc/${dataset}/collections/${collectionId}/items/`,
    params: {...params},
    ...otherOptions,
  };

  if (!config.params.limit) {
    config.params.limit = 250; // default so we can bail early on incomplete pages
  }

  if (!config.maxItems) {
    config.maxItems = config.params.limit;
  }

  return pager(config, 'features', config.each);
};

export const getFeatureByReference = (reference, options) => {
  const parsed = parseReference(reference);

  let url = `/ogc/${parsed.dataset}/collections/${parsed.collectionId}/items/${parsed.featureId}`;

  if (parsed.version) {
    url = `${url}/versions/${parsed.version}`;
  }
  // return the features
  return graneroInstance.get(url, options).then(r => r.data);
};

export const addFeatureToCollection = (collectionId, feature, options) => {
  const {dataset = DEFAULT_DATASET, ...config} = options || {};

  return graneroInstance
    .post(`/ogc/${dataset}/collections/${collectionId}/items/`, feature, config)
    .then(res => res.data);
};

export const updateFeature = (feature, options) => {
  const config = options || {};

  const featureRef = getFeatureReference(feature);
  const featureHref = featureRef
    ? referenceToUrl(featureRef)
    : getLink(feature, 'self');

  if (!featureHref) {
    throw new Error('Feature is missing `links` and no short ref provided.');
  }

  return graneroInstance
    .put(featureHref, feature, config)
    .then(res => res.data);
};

/**
 *
 * @param {Array} references - Array of short URLs
 * @returns {Array} - Array of geojson features
 */
export const getFeaturesByReferences = (references, options) => {
  if (!Array.isArray(references)) {
    throw new Error('References must be of type Array');
  }

  const url = `/process/multifetch/execution`;

  // return the features
  return processAPIInstance.post(url, references, options).then(r => r.data);
};

export const getFeaturesUnionByReferences = (references, options) => {
  if (!Array.isArray(references)) {
    throw new Error('References must be of type Array');
  }

  const url = `/process/union/execution`;

  // return the features
  return processAPIInstance.post(url, references, options).then(r => r.data);
};

export const deleteFeature = (featureRef, options) => {
  const href = referenceToUrl(featureRef);

  return graneroInstance.delete(href, options).then(res => res.data);
};
